<script>
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import LoadingOverlay from "./LoadingOverlay.vue";
import { mapActions, mapGetters } from "vuex";
import { VerificationResultStatus } from "@/models/verificationConstants";
export default {
  components: { InputText, Checkbox, LoadingOverlay, Dialog },
  name: "SubscribeModal",
  props: [],
  data() {
    return {
      newEmails: [""],
      newPhoneNumbers: [""],
      emails: [],
      phoneNumbers: [],
      agree: false,
      showUnsubscribeConfirmationDialog: false,
      unsubscribeConfirmationContact: {},
      showResetConfirmationDialog: false,
      resetConfirmationContact: {},
      showVerifyDialog: false,
      verifyContact: {},
      verificationCode: "",
      verificationError: "",
      showSubscriptionSuccessMessage: false,
      hasEmailSubscriptions: false,
      hasSMSSubscriptions: false,
      hasAttemptedSubmit: false,
      subscriptionError: "",
      showSubscriptionErrorMessage: false,
      bodyObserver: null,
    };
  },
  created() {
    this.GET_SUBSCRIPTIONS();
    //Fix nested modals adding back the scroll bar on close
    this.bodyObserver = new MutationObserver(() => {
      if (!document.body.classList.contains("p-overflow-hidden")) {
        document.body.classList.add("p-overflow-hidden");
      }
    });
    this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  },
  beforeUnmount() {
    if (this.bodyObserver) {
      this.bodyObserver.disconnect();
    }
  },
  computed: {
    ...mapGetters(["subscriptions", "getClientConfig"]),
    hasValidPhoneNumbers() {
      return this.newPhoneNumbers.some(phone => phone && phone.trim().length > 0);
    },
    hasValidEmails() {
      return this.newEmails.some(email => email && email.trim().length > 0);
    },
    phoneNumberErrors() {
      return this.newPhoneNumbers.map(phone => {
        if (!phone || phone.trim().length === 0) {
          return null; // Empty is okay, user might not fill all slots
        }
        const digits = phone.replace(/\D/g, "");
        if (digits.length !== 10) {
          return this.$t("SubscribeModal.phoneNumberError");
        }
        return null;
      });
    },
    emailErrors() {
      return this.newEmails.map(email => {
        if (!email || email.trim().length === 0) {
          return null; // Empty is okay, user might not fill all slots
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          return this.$t("SubscribeModal.emailError");
        }
        return null;
      });
    },
    phoneNumberDuplicateErrors() {
      const existingPhones = new Set(this.phoneNumbers.map(p => p.contact.replace(/\D/g, "")));
      return this.newPhoneNumbers.map((phone, index) => {
        if (!phone || phone.trim().length === 0) {
          return null;
        }
        const digits = phone.replace(/\D/g, "");

        if (existingPhones.has(digits)) {
          return this.$t("SubscribeModal.duplicatePhoneError");
        }

        for (let i = 0; i < index; i++) {
          const otherDigits = this.newPhoneNumbers[i].replace(/\D/g, "");
          if (otherDigits.length === 10 && otherDigits === digits) {
            return this.$t("SubscribeModal.duplicatePhoneErrorNew");
          }
        }

        return null;
      });
    },
    emailDuplicateErrors() {
      const existingEmails = new Set(this.emails.map(e => e.contact.toLowerCase()));
      return this.newEmails.map((email, index) => {
        if (!email || email.trim().length === 0) {
          return null;
        }

        if (existingEmails.has(email.toLowerCase())) {
          return this.$t("SubscribeModal.duplicateEmailError");
        }

        for (let i = 0; i < index; i++) {
          if (this.newEmails[i].toLowerCase() === email.toLowerCase()) {
            return this.$t("SubscribeModal.duplicateEmailErrorNew");
          }
        }

        return null;
      });
    },
    hasAnyErrors() {
      return (
        this.phoneNumberErrors.some(error => error !== null) ||
        this.emailErrors.some(error => error !== null) ||
        this.phoneNumberDuplicateErrors.some(error => error !== null) ||
        this.emailDuplicateErrors.some(error => error !== null)
      );
    },
  },
  methods: {
    ...mapActions([
      "GET_SUBSCRIPTIONS",
      "CREATE_SUBSCRIPTIONS",
      "REMOVE_SUBSCRIPTION",
      "VERIFY_SUBSCRIPTION",
      "RESET_SUBSCRIPTION",
    ]),
    formatPhone(num) {
      const digits = num.replace(/\D/g, "");
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    },
    handlePhoneInput(index, event) {
      let value = event.target.value.replace(/\D/g, "");
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
      let formatted = value;
      if (value.length > 0) {
        if (value.length <= 3) {
          formatted = "(" + value;
        } else if (value.length <= 6) {
          formatted = "(" + value.slice(0, 3) + ") " + value.slice(3);
        } else {
          formatted = "(" + value.slice(0, 3) + ") " + value.slice(3, 6) + "-" + value.slice(6);
        }
      }
      this.newPhoneNumbers[index] = formatted;
    },
    addNewEmailSlot() {
      // Check for validation errors in current entries
      if (
        this.emailErrors.some(error => error !== null) ||
        this.emailDuplicateErrors.some(error => error !== null)
      ) {
        this.hasAttemptedSubmit = true;
        return; // Don't add a new slot if there are errors
      }
      this.newEmails.push("");
      this.hasAttemptedSubmit = false;
    },
    addNewPhoneSlot() {
      // Check for validation errors in current entries
      if (
        this.phoneNumberErrors.some(error => error !== null) ||
        this.phoneNumberDuplicateErrors.some(error => error !== null)
      ) {
        this.hasAttemptedSubmit = true;
        return; // Don't add a new slot if there are errors
      }
      this.newPhoneNumbers.push("");
      this.hasAttemptedSubmit = false;
    },
    removeNewPhone(index) {
      this.newPhoneNumbers.splice(index, 1);
    },
    removeNewEmail(index) {
      this.newEmails.splice(index, 1);
    },
    removeContact(contact) {
      this.showUnsubscribeConfirmationDialog = true;
      this.unsubscribeConfirmationContact = contact;
    },
    confirmUnsubscribe() {
      this.REMOVE_SUBSCRIPTION(this.unsubscribeConfirmationContact).then(() => {
        this.removeContactFromList(this.unsubscribeConfirmationContact);
        this.showUnsubscribeConfirmationDialog = false;
        this.unsubscribeConfirmationContact = {};
      });
    },
    removeContactFromList(contact) {
      if (contact.contactType === "sms") {
        const removeIndex = this.phoneNumbers.findIndex(c => c.contact === contact.contact);
        this.phoneNumbers.splice(removeIndex, 1);
      } else if (contact.contactType === "email") {
        const removeIndex = this.emails.findIndex(c => c.contact === contact.contact);
        this.emails.splice(removeIndex, 1);
      }
    },
    cancelUnsubscribe() {
      this.showUnsubscribeConfirmationDialog = false;
      this.unsubscribeConfirmationContact = {};
    },
    openVerifyDialog(contact) {
      this.showVerifyDialog = true;
      this.verifyContact = contact;
      this.verificationCode = "";
      this.verificationError = "";
    },
    confirmVerify() {
      this.verificationError = "";
      this.VERIFY_SUBSCRIPTION({
        subscription: {
          ...this.verifyContact,
          client: this.getClientConfig.clientname,
        },
        verificationCode: this.verificationCode,
      })
        .then(result => {
          if (
            result === VerificationResultStatus.Verified ||
            result === VerificationResultStatus.AlreadyVerified
          ) {
            if (this.verifyContact.contactType === "sms") {
              const contact = this.phoneNumbers.find(c => c.contact === this.verifyContact.contact);
              if (contact) {
                contact.verified = true;
              }
            } else if (this.verifyContact.contactType === "email") {
              const contact = this.emails.find(c => c.contact === this.verifyContact.contact);
              if (contact) {
                contact.verified = true;
              }
            }

            this.closeVerifyDialog();
          } else {
            this.verificationError = this.$t("SubscribeModal.invalidVerificationCode");
          }
        })
        .catch(() => {
          this.verificationError = this.$t("SubscribeModal.verificationError");
        });
    },
    closeVerifyDialog() {
      this.showVerifyDialog = false;
      this.verifyContact = {};
      this.verificationCode = "";
      this.verificationError = "";
    },
    resendCodeFromDialog() {
      this.resetCode(this.verifyContact);
      this.closeVerifyDialog();
    },
    isCodeExpired(contact) {
      if (!contact.updatedAt) {
        return false;
      }
      const updatedAt = new Date(contact.updatedAt);
      const now = new Date();
      const hoursDiff = (now - updatedAt) / (1000 * 60 * 60);
      return hoursDiff >= 24;
    },
    resetCode(contact) {
      this.showResetConfirmationDialog = true;
      this.resetConfirmationContact = contact;
    },
    confirmReset() {
      this.RESET_SUBSCRIPTION(this.resetConfirmationContact).then(() => {
        if (this.resetConfirmationContact.contactType === "sms") {
          const contact = this.phoneNumbers.find(
            c => c.contact === this.resetConfirmationContact.contact
          );
          if (contact) {
            contact.updatedAt = new Date().toISOString();
          }
        } else if (this.resetConfirmationContact.contactType === "email") {
          const contact = this.emails.find(
            c => c.contact === this.resetConfirmationContact.contact
          );
          if (contact) {
            contact.updatedAt = new Date().toISOString();
          }
        }
        this.showResetConfirmationDialog = false;
        this.resetConfirmationContact = {};
      });
    },
    cancelReset() {
      this.showResetConfirmationDialog = false;
      this.resetConfirmationContact = {};
    },
    goBack() {
      this.showSubscriptionSuccessMessage = false;
      this.showSubscriptionErrorMessage = false;
      this.subscriptionError = "";
      this.newEmails = [""];
      this.newPhoneNumbers = [""];
      this.agree = false;
      this.hasAttemptedSubmit = false;
      this.GET_SUBSCRIPTIONS();
    },
    setupSubscriptions() {
      this.hasAttemptedSubmit = true;

      // Check if there are any validation errors
      if (this.hasAnyErrors) {
        return; // Don't proceed if there are errors
      }

      const createSubscriptionsRequest = {
        SMSSubscriptions: [],
        EmailSubscriptions: [],
      };
      this.newEmails.map(emailContact => {
        if (emailContact.trim().length > 0) {
          createSubscriptionsRequest.EmailSubscriptions.push({
            Email: emailContact,
          });
        }
      });

      this.newPhoneNumbers.map(smsContact => {
        const rawSmsContact = smsContact.replace(/\D/g, "");
        if (rawSmsContact.trim().length > 0) {
          createSubscriptionsRequest.SMSSubscriptions.push({
            phoneNumber: rawSmsContact,
          });
        }
      });

      this.hasEmailSubscriptions = createSubscriptionsRequest.EmailSubscriptions.length > 0;
      this.hasSMSSubscriptions = createSubscriptionsRequest.SMSSubscriptions.length > 0;

      this.CREATE_SUBSCRIPTIONS(createSubscriptionsRequest)
        .then(response => {
          if (response && response.success) {
            this.showSubscriptionSuccessMessage = true;
          } else {
            this.subscriptionError = this.$t("SubscribeModal.subscriptionError");
            this.showSubscriptionErrorMessage = true;
          }
        })
        .catch(() => {
          this.subscriptionError = this.$t("SubscribeModal.subscriptionError");
          this.showSubscriptionErrorMessage = true;
        });
    },
  },
  watch: {
    subscriptions(newVal) {
      this.emails = [];
      this.phoneNumbers = [];
      newVal.map(contact => {
        if (contact.contactType === "email") {
          this.emails.push(contact);
        } else if (contact.contactType === "sms") {
          this.phoneNumbers.push(contact);
        }
      });
    },
  },
};
</script>

<template>
  <div class="modal-container">
    <div id="main-dialog" v-if="!showSubscriptionSuccessMessage && !showSubscriptionErrorMessage">
      <p class="subscribe-header-message">
        {{ $t("SubscribeModal.headerMessage") }}
      </p>

      <div class="contact-container">
        <div class="sms-contact-container">
          <h3 class="contact-container-header">
            {{ $t("SubscribeModal.subscribeToTextMessages") }}
          </h3>

          <div v-for="(contact, i) in phoneNumbers" :key="i" class="contact-item-container">
            <div class="contact-item-first-row">
              <label class="contact-item-label">{{
                $t("SubscribeModal.parentPhoneNumber", { number: i + 1 })
              }}</label>
              <button
                v-if="contact.verified"
                class="button-link unsubscribe-button"
                @click="removeContact(contact)"
              >
                {{ $t("SubscribeModal.unsubscribe") }}
              </button>
              <button v-else class="button-link remove-button" @click="removeContact(contact)">
                {{ $t("SubscribeModal.remove") }}
              </button>
            </div>
            <div class="contact-item-second-row">
              <p class="contact-item-contact">{{ formatPhone(contact.contact) }}</p>
              <div v-if="!contact.verified" class="verify-button-container">
                <span class="material-icons verify-button-icon">warning</span>
                <button
                  v-if="!isCodeExpired(contact)"
                  class="button-link verify-button"
                  @click="openVerifyDialog(contact)"
                >
                  {{ $t("SubscribeModal.verify") }}
                </button>
                <button v-else class="button-link verify-button" @click="resetCode(contact)">
                  {{ $t("SubscribeModal.reset") }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="phoneNumbers.length < 3">
            <div v-for="(_, i) in newPhoneNumbers" :key="i" class="contact-item-container">
              <div class="contact-item-first-row">
                <label for="sms" class="contact-item-label">{{
                  $t("SubscribeModal.parentPhoneNumber", {
                    number:
                      phoneNumbers.length + newPhoneNumbers.length > 1
                        ? phoneNumbers.length + 1 + i
                        : "",
                  })
                }}</label>
                <button
                  v-if="newPhoneNumbers.length > 1"
                  class="button-link remove-button"
                  @click="removeNewPhone(i)"
                >
                  {{ $t("SubscribeModal.remove") }}
                </button>
              </div>
              <InputText
                :id="`sms-${i}`"
                v-model="newPhoneNumbers[i]"
                :placeholder="$t('SubscribeModal.phonePlaceholder')"
                @input="handlePhoneInput(i, $event)"
                type="tel"
              />
              <div v-if="hasAttemptedSubmit && phoneNumberErrors[i]" class="validation-error">
                {{ phoneNumberErrors[i] }}
              </div>
              <div
                v-if="hasAttemptedSubmit && phoneNumberDuplicateErrors[i]"
                class="validation-error"
              >
                {{ phoneNumberDuplicateErrors[i] }}
              </div>
              <Button
                v-if="
                  i == newPhoneNumbers.length - 1 &&
                  phoneNumbers.length + newPhoneNumbers.length < 3
                "
                :label="$t('SubscribeModal.addMore')"
                class="contact-add-more-button"
                @click="addNewPhoneSlot"
              />
            </div>
          </div>
        </div>

        <div class="email-contact-container">
          <h3 class="contact-container-header">{{ $t("SubscribeModal.subscribeToEmails") }}</h3>

          <div v-for="(contact, i) in emails" :key="i" class="contact-item-container">
            <div class="contact-item-first-row">
              <label class="contact-item-label">{{
                $t("SubscribeModal.parentEmailAddress", { number: i + 1 })
              }}</label>
              <button
                v-if="contact.verified"
                class="button-link unsubscribe-button"
                @click="removeContact(contact)"
              >
                {{ $t("SubscribeModal.unsubscribe") }}
              </button>
              <button v-else class="button-link remove-button" @click="removeContact(contact)">
                {{ $t("SubscribeModal.remove") }}
              </button>
            </div>
            <div class="contact-item-second-row">
              <p class="contact-item-contact">{{ contact.contact }}</p>
              <div v-if="!contact.verified" class="verify-button-container">
                <span class="material-icons verify-button-icon">warning</span>
                <button
                  v-if="!isCodeExpired(contact)"
                  class="button-link verify-button"
                  @click="openVerifyDialog(contact)"
                >
                  {{ $t("SubscribeModal.verify") }}
                </button>
                <button v-else class="button-link verify-button" @click="resetCode(contact)">
                  {{ $t("SubscribeModal.reset") }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="emails.length < 3">
            <div v-for="(_, i) in newEmails" :key="i" class="contact-item-container">
              <div class="contact-item-first-row">
                <label for="email" class="contact-item-label">{{
                  $t("SubscribeModal.parentEmailAddress", {
                    number: emails.length + newEmails.length > 1 ? emails.length + 1 + i : "",
                  })
                }}</label>
                <button
                  v-if="newEmails.length > 1"
                  class="button-link remove-button"
                  @click="removeNewEmail(i)"
                >
                  {{ $t("SubscribeModal.remove") }}
                </button>
              </div>
              <InputText
                id="email"
                v-model="newEmails[i]"
                :placeholder="$t('SubscribeModal.emailPlaceholder')"
              />
              <div v-if="hasAttemptedSubmit && emailErrors[i]" class="validation-error">
                {{ emailErrors[i] }}
              </div>
              <div v-if="hasAttemptedSubmit && emailDuplicateErrors[i]" class="validation-error">
                {{ emailDuplicateErrors[i] }}
              </div>

              <Button
                v-if="i == newEmails.length - 1 && emails.length + newEmails.length < 3"
                :label="$t('SubscribeModal.addMore')"
                class="contact-add-more-button"
                @click="addNewEmailSlot"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="subscribe-footer-container">
        <div class="terms-text">
          {{ $t("SubscribeModal.termsText") }}
          <a :href="getClientConfig.privacyPolicyTermsUrl" target="_blank" class="link">{{
            $t("SubscribeModal.privacyPolicyTerms")
          }}</a>
        </div>
        <div>
          <Checkbox v-model="agree" :binary="true" />
          <label style="margin-left: 0.5rem"> {{ $t("SubscribeModal.acceptTerms") }} </label>
        </div>
        <div class="subscribe-button-container">
          <Button
            :label="$t('SubscribeModal.subscribe')"
            class="subscribe-button"
            severity="success"
            :disabled="
              !agree ||
              !(hasValidPhoneNumbers || hasValidEmails) ||
              (hasAttemptedSubmit && hasAnyErrors)
            "
            @click="setupSubscriptions"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="showSubscriptionSuccessMessage" class="subscription-success-container">
        <div v-if="hasSMSSubscriptions" class="success-message">
          <p>{{ $t("SubscribeModal.textSent") }}</p>
          <p>{{ $t("SubscribeModal.confirmSubscriptionDirections") }}</p>
          <p>{{ $t("SubscribeModal.verificationCodeExpirationWarning") }}</p>
        </div>
        <div v-if="hasEmailSubscriptions" class="success-message">
          <p>{{ $t("SubscribeModal.emailSent") }}</p>
          <p>{{ $t("SubscribeModal.confirmSubscriptionDirections") }}</p>
          <p>{{ $t("SubscribeModal.verificationCodeExpirationWarning") }}</p>
        </div>
      </div>
      <div v-else-if="showSubscriptionErrorMessage" class="subscription-error-container">
        <p class="subscription-error-message">{{ subscriptionError }}</p>
      </div>
      <div class="success-footer-container">
        <Button
          :label="$t('SubscribeModal.back')"
          class="back-button"
          severity="secondary"
          @click="goBack"
        />
        <div class="verification-hint-message">
          <p v-if="showSubscriptionSuccessMessage">
            {{ $t("SubscribeModal.confirmationBackButtonMessage") }}
          </p>
        </div>
      </div>
    </div>

    <LoadingOverlay />
    <Dialog
      :visible="showUnsubscribeConfirmationDialog"
      @update:visible="showUnsubscribeConfirmationDialog = $event"
      modal
      :header="
        unsubscribeConfirmationContact.verified
          ? $t('SubscribeModal.unsubscribeConfirmation')
          : $t('SubscribeModal.removeConfirmation')
      "
      closeOnEscape
    >
      <div class="unsubscribe-confirmation-container">
        <div>
          {{
            unsubscribeConfirmationContact.verified
              ? $t("SubscribeModal.unsubscribePrompt", {
                  contact:
                    unsubscribeConfirmationContact.contactType === "sms"
                      ? formatPhone(unsubscribeConfirmationContact.contact)
                      : unsubscribeConfirmationContact.contact,
                })
              : $t("SubscribeModal.removePrompt", {
                  contact:
                    unsubscribeConfirmationContact.contactType === "sms"
                      ? formatPhone(unsubscribeConfirmationContact.contact)
                      : unsubscribeConfirmationContact.contact,
                })
          }}
        </div>
        <div class="unsubscribe-confirmation-buttons">
          <Button
            class="unsubscribe-confirmation-confirm-button"
            :label="$t('SubscribeModal.confirm')"
            severity="success"
            @click="confirmUnsubscribe"
          />
          <Button
            :label="$t('SubscribeModal.cancel')"
            severity="danger"
            @click="cancelUnsubscribe"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      :visible="showResetConfirmationDialog"
      @update:visible="showResetConfirmationDialog = $event"
      modal
      :header="$t('SubscribeModal.resetVerificationCode')"
      closeOnEscape
    >
      <div class="unsubscribe-confirmation-container">
        <div>
          {{
            $t("SubscribeModal.resetVerificationPrompt", {
              contact:
                resetConfirmationContact.contactType === "sms"
                  ? formatPhone(resetConfirmationContact.contact)
                  : resetConfirmationContact.contact,
            })
          }}
        </div>
        <div class="unsubscribe-confirmation-buttons">
          <Button
            class="unsubscribe-confirmation-confirm-button"
            :label="$t('SubscribeModal.confirm')"
            severity="success"
            @click="confirmReset"
          />
          <Button :label="$t('SubscribeModal.cancel')" severity="danger" @click="cancelReset" />
        </div>
      </div>
    </Dialog>
    <Dialog
      :visible="showVerifyDialog"
      @update:visible="showVerifyDialog = $event"
      modal
      :header="$t('SubscribeModal.verifyContact')"
      closeOnEscape
    >
      <div class="verify-dialog-container">
        <div class="verify-dialog-message">
          {{
            $t("SubscribeModal.enterVerificationCode", {
              contact:
                verifyContact.contactType === "sms"
                  ? formatPhone(verifyContact.contact)
                  : verifyContact.contact,
            })
          }}
        </div>
        <div class="verify-dialog-input-container">
          <label for="verificationCode" class="verify-dialog-label">{{
            $t("SubscribeModal.verificationCode")
          }}</label>
          <InputText
            id="verificationCode"
            v-model="verificationCode"
            :placeholder="$t('SubscribeModal.enterCode')"
            class="verify-dialog-input"
          />
          <Button
            :label="$t('SubscribeModal.resetCode')"
            severity="secondary"
            text
            @click="resendCodeFromDialog"
            class="resend-code-button"
          />
          <div v-if="verificationError" class="verify-dialog-error">
            {{ verificationError }}
          </div>
        </div>
        <div class="verify-dialog-buttons">
          <Button
            class="verify-dialog-confirm-button"
            :label="$t('SubscribeModal.verify')"
            severity="success"
            :disabled="verificationCode.length === 0"
            @click="confirmVerify"
          />
          <Button
            :label="$t('SubscribeModal.cancel')"
            severity="danger"
            @click="closeVerifyDialog"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.modal-container {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-height: 50vh;
}

.subscribe-header {
  font-size: 2rem;
  color: black;
  font-weight: bolder;
}

.subscribe-header-message {
  color: black;
  margin-bottom: 1.5rem;
}

.contact-container {
  display: flex;
  flex-direction: row;
}

.sms-contact-container {
  width: 40%;
  display: flex;
  flex-direction: column;
  margin-right: 2rem;
}

.email-contact-container {
  width: 60%;
  display: flex;
  flex-direction: column;
}

.contact-container-header {
  font-size: 1.25rem;
  color: black;
  font-weight: bolder;
}

.contact-item-label {
  font-size: 1rem;
  font-weight: bolder;
  color: black;
}

.contact-add-more-button {
  margin-top: 1rem;
  background-color: #485056;
  border-radius: 5px;
  max-width: 10rem;
}

.subscribe-footer-container {
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  border-top: 1px black solid;
  padding-top: 2rem;
}

.terms-text {
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.subscribe-button-container {
  margin-top: 1rem;
}

.subscribe-button {
  border-radius: 5px;
  max-width: 10rem;
}

.contact-item-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-bottom: 1rem;
  margin-top: 1rem;
  border-bottom: 1px solid black;
}

.contact-item-first-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.contact-item-second-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.verify-button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #b82021;
}

.verify-button {
  color: #b82021;
}

.verify-button-icon {
  margin-right: 0.25rem;
}

.remove-button {
  color: #005c99;
}

.unsubscribe-button {
  color: #b82021;
}

.button-link {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
}

.contact-item-container:last-child {
  border-bottom: none;
}

.contact-item-contact {
  font-size: 1rem;
  color: black;
  overflow-wrap: break-word;
  word-break: break-word;
  flex: 1;
  margin-right: 1rem;
}

.unsubscribe-confirmation-container {
  display: flex;
  flex-direction: column;
}

.unsubscribe-confirmation-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 1rem;
}

.unsubscribe-confirmation-confirm-button {
  margin-right: 1rem;
}

.verify-dialog-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.verify-dialog-message {
  font-size: 1rem;
  color: black;
}

.verify-dialog-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.verify-dialog-label {
  font-weight: bold;
  font-size: 0.9rem;
}

.verify-dialog-input {
  width: 100%;
}

.verify-dialog-error {
  color: #b82021;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.verify-dialog-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.verify-dialog-confirm-button {
  margin-right: 0.5rem;
}

.subscription-success-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.subscription-error-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.subscription-error-message {
  color: #b82021;
  font-weight: 500;
  font-size: 1rem;
  margin: 0;
}

.success-message {
  line-height: 1.6;
}

.success-message p {
  margin: 0.25rem 0;
}

.validation-error {
  color: #b82021;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  font-weight: 500;
}

.verification-hint-message {
  font-size: 0.9rem;
  color: #555;
  font-style: italic;
  margin-left: 1rem;
}

.verification-hint-message p {
  margin: 0;
}

.success-footer-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 2rem;
  border-top: 1px black solid;
  padding-top: 1.5rem;
}

.back-button {
  border-radius: 5px;
}
</style>
